import { createContext, useContext, useState, useEffect } from "react";
import { IngredientUnitApi } from "../api/IngredientUnitApi";
import { logger } from "../utils/logger";
import { getErrorMessage } from "../utils/errorMessages";
import { toast } from "sonner";

const IngredientUnitContext = createContext();

export const useIngredientUnits = () => {
  return useContext(IngredientUnitContext);
};

export const IngredientUnitProvider = ({ children }) => {
  const [ingredientUnits, setIngredientUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchIngredientUnits = async () => {
    try {
      setLoading(true);
      const response = await IngredientUnitApi.fetchAll();
      setIngredientUnits(response);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to fetch ingredient units", error, {
        correlationId,
      });
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const addIngredientUnit = async (unit) => {
    try {
      const created = await IngredientUnitApi.create({ unit });
      setIngredientUnits((prev) => [...prev, created]);
      toast.success("Ingredient unit created");
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to create ingredient unit", error, {
        correlationId,
        unit,
      });
      toast.error(getErrorMessage(error));
    }
  };

  const removeIngredientUnit = async (unitId) => {
    try {
      await IngredientUnitApi.deleteById(unitId);
      setIngredientUnits((prev) => prev.filter((u) => u.unitId !== unitId));
      toast.success("Ingredient unit deleted");
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to delete ingredient unit", error, {
        correlationId,
        unitId,
      });
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    fetchIngredientUnits();
  }, []);

  const value = {
    ingredientUnits,
    loading,
    addIngredientUnit,
    removeIngredientUnit,
  };

  return (
    <IngredientUnitContext.Provider value={value}>
      {children}
    </IngredientUnitContext.Provider>
  );
};
